/**
 * PII scrub for outbound telemetry payloads.
 *
 * Boundary: in-process devtool bus carries raw payloads (engineer-local).
 * Every EXTERNAL sink (Better Stack OTLP) MUST run payloads through this
 * scrub first so Solana pubkeys / signatures never leave the device in
 * plaintext.
 *
 * What gets scrubbed:
 *   - `queryKey` element-by-element. Strings matching the Solana base58
 *     pubkey (43-44 chars) or signature (87-88 chars) regex are replaced
 *     with `e_<8 hex>` where `8 hex` is the FNV-1a-64 hash of the value.
 *   - Nested object segments inside `queryKey`: each string-valued property
 *     gets the same treatment.
 *   - `subject` (NATS subject strings): scrubbed segment-by-segment so the
 *     registry shape stays legible (e.g.
 *     `morfi.market.e_abc12345.trade`).
 *   - `componentName` + `fiberPath`: dropped entirely in production mode
 *     (kept in dev for fiber attribution).
 *
 * Determinism: FNV-1a is deterministic across runs and platforms, so
 * dashboards still aggregate sanely. The hash is NOT a security boundary
 * (8 hex chars = 32 bits = trivially rainbow-tabled by anyone with the
 * pubkey set); it preserves SHAPE for aggregation while obscuring the
 * raw value over the wire. Anyone who needs strong anonymisation should
 * not send the value in the first place.
 *
 * Migration note: prior implementation used SHA-256 via `node:crypto`,
 * which was a Node-only import and broke React Native. FNV-1a is a sync
 * pure-JS hash; the `e_<8hex>` output shape is preserved.
 */

const BASE58_PUBKEY_RE = /^[1-9A-HJ-NP-Za-km-z]{43,44}$/;
const BASE58_SIGNATURE_RE = /^[1-9A-HJ-NP-Za-km-z]{87,88}$/;

function isBase58EntityLike(s: string): boolean {
  return BASE58_PUBKEY_RE.test(s) || BASE58_SIGNATURE_RE.test(s);
}

/**
 * FNV-1a 64-bit. ~10 LOC, dep-free, RN-safe.
 *
 *   FNV_OFFSET_64 = 14695981039346656037n  (0xcbf29ce484222325n)
 *   FNV_PRIME_64  = 1099511628211n         (0x100000001b3n)
 *
 * The 64-bit BigInt math is well within Hermes' BigInt support tier.
 */
const FNV_OFFSET_64 = 0xcbf29ce484222325n;
const FNV_PRIME_64 = 0x100000001b3n;
const FNV_MASK_64 = (1n << 64n) - 1n;

export function fnv1a64(value: string): bigint {
  let hash = FNV_OFFSET_64;
  for (let i = 0; i < value.length; i++) {
    hash ^= BigInt(value.charCodeAt(i));
    hash = (hash * FNV_PRIME_64) & FNV_MASK_64;
  }
  return hash;
}

/**
 * Return `len` lowercase hex chars of fnv1a64(value). Default 8 = 32 bits of
 * distinguishing power; matches the legacy SHA-256-prefix output shape so
 * dashboards keying off `e_<8hex>` continue to bucket correctly.
 */
export function hashEntity(value: string, len = 8): string {
  const h = fnv1a64(value);
  return h.toString(16).padStart(16, '0').slice(0, len);
}

function scrubString(s: string): string {
  if (isBase58EntityLike(s)) return `e_${hashEntity(s)}`;
  return s;
}

function scrubQueryKey(key: ReadonlyArray<unknown>): ReadonlyArray<unknown> {
  return key.map((segment): unknown => {
    if (typeof segment === 'string') return scrubString(segment);
    if (segment !== null && typeof segment === 'object' && !Array.isArray(segment)) {
      const obj = segment as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(obj)) {
        out[k] = typeof v === 'string' ? scrubString(v) : v;
      }
      return out;
    }
    return segment;
  });
}

function scrubSubject(subject: string): string {
  return subject
    .split('.')
    .map((seg) => (isBase58EntityLike(seg) ? `e_${hashEntity(seg)}` : seg))
    .join('.');
}

/** Single supported sink — only Better Stack OTel pipeline emits externally. */
export type ExternalSink = 'better-stack';

export interface ScrubbablePayload {
  queryKey?: ReadonlyArray<unknown>;
  subject?: string;
  componentName?: string;
  fiberPath?: string;
  correlationId?: string;
  readonly [k: string]: unknown;
}

interface ProcessEnvLike {
  env: { NODE_ENV?: string };
}

function defaultIsProd(): boolean {
  const proc = (globalThis as { process?: ProcessEnvLike }).process;
  return proc?.env?.NODE_ENV === 'production';
}

/**
 * Scrub a payload before it leaves the device. Idempotent — scrubbing
 * twice produces the same result (because the regex matches base58 input,
 * not the `e_<hex>` output).
 */
export function scrubForSink(
  payload: ScrubbablePayload,
  _sink: ExternalSink,
  isProd: () => boolean = defaultIsProd,
): ScrubbablePayload {
  const out: Record<string, unknown> = { ...payload };
  if (Array.isArray(out.queryKey)) {
    out.queryKey = scrubQueryKey(out.queryKey as ReadonlyArray<unknown>);
  }
  if (typeof out.subject === 'string') {
    out.subject = scrubSubject(out.subject);
  }
  if (isProd()) {
    // Fiber attribution is engineer-only context; never reaches Better Stack.
    delete out.componentName;
    delete out.fiberPath;
  }
  return out as ScrubbablePayload;
}

/**
 * Scrub a flat attribute record (the kind that goes into OtlpKeyValue lists).
 * Same rules as scrubForSink but operates on a string-keyed map of primitives.
 */
export function scrubAttributes(
  attrs: Readonly<Record<string, string | number | boolean | undefined>>,
  isProd: () => boolean = defaultIsProd,
): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(attrs)) {
    if (isProd() && (k === 'componentName' || k === 'fiberPath' || k === 'morfi.fiber_path')) {
      continue;
    }
    if (typeof v === 'string') {
      if (k === 'morfi.subject' || k === 'subject') out[k] = scrubSubject(v);
      else out[k] = scrubString(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

let warnedThisSession = false;

/**
 * Operator-facing tripwire: fires once per session if the prod build has
 * the devtool flag enabled. Engineer-impersonated prod sessions forward
 * sampled events with PII scrub applied; the warning surfaces the choice.
 */
export function warnIfDevtoolFlagOnInProd(
  isProd: () => boolean = defaultIsProd,
  flagOn: () => boolean = (): boolean =>
    Boolean((globalThis as { __MORFI_DEVTOOL_ENABLED__?: boolean }).__MORFI_DEVTOOL_ENABLED__),
  warn: (m: string) => void = (m: string): void => {
    console.warn(m);
  },
): void {
  if (warnedThisSession) return;
  if (!isProd() || !flagOn()) return;
  warnedThisSession = true;
  warn(
    '__MORFI_DEVTOOL_ENABLED__ is true in a production build; ' +
      'engineer-impersonated sessions forward sampled events with PII-scrub applied. ' +
      'Do NOT enable this flag in shared-session prod builds.',
  );
}

/** Test-only reset — exported so vitest can clear the once-flag. */
export function __resetWarnedFlagForTests(): void {
  warnedThisSession = false;
}
